import {
  CONFIGURATOR_CORRESPONDENCE_FIELDS,
  EConfiguratorQuestionType,
  IConfiguratorAnswers,
  IConfiguratorInvitee,
  IConfiguratorInvoiceAddress,
  IConfiguratorSection
} from '../models/configurator.model';
import {
  formatAddressLine,
  getEffectiveAddress,
  getFilledInvitees,
  getInvoiceName,
  isAddressAnswered,
  isInviteeComplete,
  isInvoiceAddressComplete
} from '../helpers/configurator-contact.helper';
import {
  COMPANY_ROLE_OPTIONS,
  configuratorIcon,
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_TIME_OPTIONS,
  CUSTOMER_TYPE_OPTIONS,
  INVOICE_RECIPIENT_CUSTOMER,
  INVOICE_RECIPIENT_OPTIONS,
  INVOICE_RECIPIENT_OTHER,
  LEAD_SOURCE_OPTIONS,
  LEAD_SOURCE_OTHER,
  LEAD_SOURCE_REFERRAL,
  SALUTATION_OPTIONS
} from './configurator-options.data';
import { isAnswer, isCompanyCustomer } from './configurator-predicates';

const T = 'ProjectConfiguratorV2.Questions';
const S = 'ProjectConfiguratorV2.Sections';

/** Wording for companies and for private customers. */
const byCustomerType =
  (company: string, personal: string) =>
  (answers: IConfiguratorAnswers): string =>
    isCompanyCustomer(answers) ? company : personal;

/** "To this address" describes itself with the address typed above. */
const describeCustomerAddress = (options = INVOICE_RECIPIENT_OPTIONS, answers: IConfiguratorAnswers) => {
  const address = formatAddressLine(getEffectiveAddress(CONFIGURATOR_CORRESPONDENCE_FIELDS, answers));
  return options.map((option) =>
    option.value === INVOICE_RECIPIENT_CUSTOMER && address ? { ...option, description: address } : option
  );
};

/**
 * Step 7 "Kontakt" for new customers. Existing customers (data from the account, earlier invoice recipients)
 * and the account lookup of invitees follow in stage 9.
 */
export const CONTACT_STEP_SECTIONS: IConfiguratorSection[] = [
  {
    id: 'contact',
    icon: configuratorIcon('person'),
    title: `${S}.contact`,
    questions: [
      {
        id: 'type',
        type: EConfiguratorQuestionType.TILES,
        summaryLabel: `${T}.customerType.summary`,
        label: `${T}.customerType.label`,
        options: CUSTOMER_TYPE_OPTIONS,
        columns: 2,
        compact: true
      },
      {
        id: 'companyName',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.companyName.label`,
        maxLength: 150,
        required: true,
        show: isCompanyCustomer
      },
      {
        id: 'companyRole',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.companyRole.label`,
        options: COMPANY_ROLE_OPTIONS,
        allowCustom: true,
        show: isCompanyCustomer
      },
      {
        id: 'salutation',
        type: EConfiguratorQuestionType.CHIPS,
        label: byCustomerType(`${T}.salutation.labelCompany`, `${T}.salutation.label`),
        options: SALUTATION_OPTIONS,
        summaryHidden: true
      },
      {
        id: 'firstName',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.firstName.label`,
        maxLength: 100,
        half: true
      },
      {
        id: 'lastName',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.lastName.label`,
        maxLength: 100,
        half: true,
        required: true
      }
    ]
  },
  {
    id: 'addressAndInvoice',
    icon: configuratorIcon('pin'),
    title: byCustomerType(`${S}.addressAndInvoiceCompany`, `${S}.addressAndInvoice`),
    highlight: true,
    questions: [
      {
        id: 'correspondenceAddress',
        type: EConfiguratorQuestionType.ADDRESS,
        summaryLabel: `${T}.correspondenceAddress.summary`,
        label: byCustomerType(`${T}.correspondenceAddress.labelCompany`, `${T}.correspondenceAddress.label`),
        addressFields: CONFIGURATOR_CORRESPONDENCE_FIELDS,
        required: true,
        requiredMessage: `${T}.correspondenceAddress.required`,
        answered: (_, answers) => isAddressAnswered(CONFIGURATOR_CORRESPONDENCE_FIELDS, answers)
      },
      {
        id: 'invoiceRecipient',
        type: EConfiguratorQuestionType.TILES,
        summaryLabel: `${T}.invoiceRecipient.summary`,
        label: `${T}.invoiceRecipient.label`,
        help: `${T}.invoiceRecipient.help`,
        options: INVOICE_RECIPIENT_OPTIONS,
        filterOptions: describeCustomerAddress,
        columns: 2,
        compact: true,
        required: true,
        requiredMessage: `${T}.invoiceRecipient.required`
      },
      {
        id: 'invoiceAddress',
        type: EConfiguratorQuestionType.INVOICE_ADDRESS,
        summaryLabel: `${T}.invoiceAddress.summary`,
        label: `${T}.invoiceAddress.label`,
        required: true,
        requiredMessage: `${T}.invoiceAddress.invalid`,
        show: isAnswer('invoiceRecipient', INVOICE_RECIPIENT_OTHER),
        answered: (value) => !!getInvoiceName(value as IConfiguratorInvoiceAddress),
        validate: (value) =>
          isInvoiceAddressComplete(value as IConfiguratorInvoiceAddress)
            ? null
            : `${T}.invoiceAddress.invalid`
      }
    ]
  },
  {
    id: 'reachability',
    icon: configuratorIcon('chat'),
    title: `${S}.reachability`,
    questions: [
      {
        id: 'phone',
        type: EConfiguratorQuestionType.PHONE,
        label: `${T}.phone.label`,
        placeholder: `${T}.phone.placeholder`,
        half: true
      },
      {
        id: 'contactChannels',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.contactChannels.summary`,
        label: `${T}.contactChannels.label`,
        options: CONTACT_CHANNEL_OPTIONS,
        multiple: true
      },
      {
        id: 'optimalContactTimeType',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.optimalContactTimeType.summary`,
        label: `${T}.optimalContactTimeType.label`,
        options: CONTACT_TIME_OPTIONS,
        multiple: true
      }
    ]
  },
  {
    id: 'invitees',
    icon: configuratorIcon('key'),
    title: `${S}.invitees`,
    note: `${S}.inviteesNote`,
    highlight: true,
    questions: [
      {
        id: 'invitees',
        type: EConfiguratorQuestionType.INVITEES,
        summaryLabel: `${T}.invitees.summary`,
        label: `${T}.invitees.label`,
        help: byCustomerType(`${T}.invitees.helpCompany`, `${T}.invitees.help`),
        inviteeRoles: isCompanyCustomer,
        answered: (value) => getFilledInvitees(value as IConfiguratorInvitee[]).length > 0,
        validate: (value) =>
          getFilledInvitees(value as IConfiguratorInvitee[]).every(isInviteeComplete)
            ? null
            : `${T}.invitees.invalid`
      }
    ]
  },
  {
    id: 'finally',
    icon: configuratorIcon('check'),
    title: `${S}.finally`,
    questions: [
      {
        id: 'leadSource',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.leadSource.summary`,
        label: `${T}.leadSource.label`,
        options: LEAD_SOURCE_OPTIONS
      },
      {
        id: 'referredBy',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.referredBy.label`,
        help: `${T}.referredBy.help`,
        placeholder: `${T}.referredBy.placeholder`,
        maxLength: 150,
        show: isAnswer('leadSource', LEAD_SOURCE_REFERRAL)
      },
      {
        id: 'leadSourceNote',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.leadSourceNote.label`,
        placeholder: `${T}.leadSourceNote.placeholder`,
        maxLength: 150,
        show: isAnswer('leadSource', LEAD_SOURCE_OTHER)
      },
      // Consent texts are fixed until they can be set per company (PLAN D7).
      {
        id: 'termsAccepted',
        type: EConfiguratorQuestionType.CHECKBOX,
        text: `${T}.termsAccepted.text`,
        required: true,
        requiredMessage: `${T}.consentRequired`,
        summaryHidden: true
      },
      {
        id: 'privacy',
        type: EConfiguratorQuestionType.CHECKBOX,
        text: `${T}.privacy.text`,
        required: true,
        requiredMessage: `${T}.consentRequired`,
        summaryHidden: true
      }
    ]
  }
];
